/**
 * Euro-Bargeld für das Barkassieren und den Kassasturz: Nennwerte mit Art
 * (Schein/Münze), Farbe der Serie und Beschriftung. Die Geldstücke werden
 * gezeichnet, nicht fotografiert (Abbildungsregeln der EZB). Beträge in Cent.
 */
export type GeldArt = 'schein' | 'muenze';

export interface Geldstueck {
  cent: number;
  art: GeldArt;
  /** Hauptfarbe (Scheine: Europa-Serie; Münzen: Gold, Bimetall, Kupfer). */
  farbe: string;
  /** Zweite Farbe (Innenfeld des Scheins bzw. Kern der Bimetall-Münze). */
  farbe2: string;
  /** Beschriftung auf dem Geldstück. */
  text: string;
}

export const GELD: readonly Geldstueck[] = [
  { cent: 50_000, art: 'schein', farbe: '#6b3c8a', farbe2: '#c9b3d8', text: '500' },
  { cent: 20_000, art: 'schein', farbe: '#c8a21a', farbe2: '#f1e39a', text: '200' },
  { cent: 10_000, art: 'schein', farbe: '#2f8f5a', farbe2: '#bfe3c9', text: '100' },
  { cent: 5_000, art: 'schein', farbe: '#e4842c', farbe2: '#f8d2ae', text: '50' },
  { cent: 2_000, art: 'schein', farbe: '#2f6fb3', farbe2: '#bcd3ee', text: '20' },
  { cent: 1_000, art: 'schein', farbe: '#c9463d', farbe2: '#f2c2bd', text: '10' },
  { cent: 500, art: 'schein', farbe: '#8c9299', farbe2: '#dfe3e7', text: '5' },
  { cent: 200, art: 'muenze', farbe: '#c0c4c9', farbe2: '#d4af37', text: '2 €' },
  { cent: 100, art: 'muenze', farbe: '#d4af37', farbe2: '#c0c4c9', text: '1 €' },
  { cent: 50, art: 'muenze', farbe: '#d4af37', farbe2: '#d4af37', text: '50' },
  { cent: 20, art: 'muenze', farbe: '#d4af37', farbe2: '#d4af37', text: '20' },
  { cent: 10, art: 'muenze', farbe: '#d4af37', farbe2: '#d4af37', text: '10' },
  { cent: 5, art: 'muenze', farbe: '#b87333', farbe2: '#b87333', text: '5' },
  { cent: 2, art: 'muenze', farbe: '#b87333', farbe2: '#b87333', text: '2' },
  { cent: 1, art: 'muenze', farbe: '#b87333', farbe2: '#b87333', text: '1' },
];

export function geldstueck(cent: number): Geldstueck | undefined {
  return GELD.find((g) => g.cent === cent);
}

/** Scheine in der Reihenfolge des Kassenbretts (klein nach groß). */
export const SCHEINE: readonly Geldstueck[] = [...GELD.filter((g) => g.art === 'schein')].reverse();
/** Münzen klein nach groß. */
export const MUENZEN: readonly Geldstueck[] = [...GELD.filter((g) => g.art === 'muenze')].reverse();

/** Gegebene Geldstücke (Cent je Tipp) zusammenzählen. */
export function summeTeile(teile: readonly number[]): number {
  return teile.reduce((s, c) => s + c, 0);
}

/** Gegebene Teile als Stückelung gruppieren (größte zuerst), z. B. 2 × 50,00 · 1 × 5,00. */
export function gruppiereTeile(teile: readonly number[]): { wertCent: number; anzahl: number }[] {
  const zaehler = new Map<number, number>();
  for (const c of teile) {
    zaehler.set(c, (zaehler.get(c) ?? 0) + 1);
  }
  return [...zaehler]
    .sort((a, b) => b[0] - a[0])
    .map(([wertCent, anzahl]) => ({ wertCent, anzahl }));
}
